import { MapPinned } from 'lucide-react'
import {
	resolveCountryLabel,
	sanitizeAddressDraft,
	type AddressDraft,
} from '#app/routes/mocra+/_form+/components/addresses/types.ts'
import { EntityCard } from '#app/routes/mocra+/_form+/components/entity-card.tsx'

type Props = {
	readonly title: string
	readonly address: AddressDraft
	readonly onEdit: () => void
	readonly onRemove?: () => void
	readonly removeDisabled?: boolean
	readonly removeLabel?: string
	readonly removeConfirmTitle?: string
	readonly removeConfirmDescription?: string
	readonly errors?: readonly string[]
}

export function AddressCard({
	title,
	address,
	onEdit,
	onRemove,
	removeDisabled,
	removeLabel = 'Remove',
	removeConfirmTitle = 'Remove this address?',
	removeConfirmDescription = 'This address will be removed from the list.',
	errors,
}: Props) {
	const sanitized = sanitizeAddressDraft(address)
	const streetLine = [sanitized.street, sanitized.street2]
		.filter(Boolean)
		.map((value) => value.trim())
		.filter(Boolean)
		.join(' • ')
	const hasErrors = Boolean(errors && errors.length > 0)
	const cardTitle = streetLine.length > 0 ? streetLine : title
	const city = sanitized.city.trim()
	const state = sanitized.state.trim()
	const postalCode = sanitized.postalCode.trim()
	const countryLabel = resolveCountryLabel(sanitized.country).trim()

	const segments: string[] = []
	if (city && state) {
		segments.push(`${city}, ${state}`)
	} else if (city) {
		segments.push(city)
	} else if (state) {
		segments.push(state)
	}
	if (postalCode) {
		segments.push(postalCode)
	}
	if (countryLabel) {
		segments.push(countryLabel)
	}

	const descriptionBase =
		segments.length > 0 ? segments.join(' • ') : 'No address set'
	const description = hasErrors
		? `${descriptionBase} • Needs attention`
		: descriptionBase

	return (
		<EntityCard
			title={cardTitle}
			description={description}
			onEdit={onEdit}
			onRemove={onRemove}
			removeDisabled={removeDisabled}
			removeLabel={removeLabel}
			removeConfirmTitle={removeConfirmTitle}
			removeConfirmDescription={removeConfirmDescription}
			icon={
				<MapPinned
					className="text-muted-foreground h-3.5 w-3.5"
					aria-hidden="true"
				/>
			}
			className={
				hasErrors
					? 'border-destructive/40 ring-destructive/40 ring-1'
					: undefined
			}
		/>
	)
}
